import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../../components/shareable/modal';
import { useDashboard } from '../../../../context/dashboardContext';

const initialSaw = {
  brand: '',
  model: '',
  work_bed_height: null,
  datum_side: '',
  price: '',
  description: '',
  brochure: '',
  saw_type: '',
  image: null,
  pdf: null,
};

const UpdateSaw = ({ isOpen, onClose, onSave, sawData, Update = "Update Saw" }) => {
  const { getSawImageData } = useDashboard();
  const [formData, setFormData] = useState(initialSaw);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [existingPdf, setExistingPdf] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);

  // Memoize the fetchExistingImage function
  const fetchExistingImage = useCallback(async (imageName) => {
    setLoadingImage(true);
    try {
      const res = await getSawImageData(imageName);
      if (res.success) {
        setImagePreview(res.url);
      }
    } catch (error) {
      console.error('Error fetching existing image:', error);
    } finally {
      setLoadingImage(false);
    }
  }, [getSawImageData]);

  // Initialize form with existing data when component mounts or sawData changes
  useEffect(() => {
    if (sawData) {
      const { image_name, brochure, ...restData } = sawData;
      
      setFormData({ ...restData, image: null, pdf: null, });
      
      // Store existing file information
      if (image_name) {
        setExistingImage(image_name);
        fetchExistingImage(image_name);
      }
      
      if (brochure) {
        setExistingPdf(brochure);
      }
    } else {
      // Reset form when no sawData
      setFormData(initialSaw);
      setExistingImage(null);
      setExistingPdf(null);
      setImagePreview(null);
    }
  }, [sawData, fetchExistingImage]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      const numValue = value === '' ? null : Number(value);
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        return;
      }
      
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData((prev) => ({ ...prev, image: file }));
      setExistingImage(null); // Clear existing image reference
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      
      const maxSize = 30 * 1024 * 1024; // 30MB
      if (file.size > maxSize) {
        alert('PDF size must be less than 30MB');
        return;
      }
      
      // Generate new filename
      const originalName = file.name;
      const originalNameWithoutExt = originalName.replace(/\.[^/.]+$/, ""); // Remove extension
      const timestamp = Date.now();
      const newFilename = `brochure_${originalNameWithoutExt}_${timestamp}.pdf`;
      
      setFormData((prev) => ({ 
        ...prev, 
        pdf: file,
        brochure: newFilename
      }));
      setExistingPdf(null); // Clear existing PDF reference
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      await onSave(formData);
      // Clean up the object URLs when done
      if (imagePreview && !imagePreview.startsWith('http')) {
        URL.revokeObjectURL(imagePreview);
      }
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, image_name:'' }));
    setExistingImage(null);
    // Clean up the object URL when removing image
    if (imagePreview && !imagePreview.startsWith('http')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setExistingImage(null); // Clear existing image reference
  };

  const removePdf = () => {
    setFormData((prev) => ({ ...prev, pdf: null, brochure: '' }));
    setExistingPdf(null); // Clear existing PDF reference
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (imagePreview && !imagePreview.startsWith('http')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

return (
  <Modal isOpen={isOpen} width='3xl' onClose={onClose}>
    <div className="flex flex-col h-[80vh]">
      <div className="p-4 border-b"><h2 className="text-lg font-semibold">{Update}</h2></div>
      <div className="overflow-y-auto flex-grow p-4">
        <form onSubmit={handleSubmit} id="update-saw-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="flex items-center">
                {formData.image ? (
                  <div className="relative w-full">
                    <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="mb-2 w-16 h-16 flex items-center justify-center overflow-hidden rounded-md bg-gray-100">
                      <img src={imagePreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="flex flex-col items-center justify-center w-full">
                        <div className="flex items-center justify-center w-full">
                        <span className="text-sm text-gray-600 text-center truncate max-w-xs">{formData.image.name}</span>
                        </div>
                        <button type="button" onClick={removeImage} className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Remove</button>
                      </div>
                    </div>
                  </div>
                ) : existingImage ? (
                  <div className="relative w-full">
                    <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="mb-2 w-16 h-16 flex items-center justify-center overflow-hidden rounded-md bg-gray-100">
                        {loadingImage ?
                        (<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>) :
                        (<img src={imagePreview} alt="Current" className="max-w-full max-h-full object-contain" onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'%3E%3C/circle%3E%3Cpolyline points='21 15 16 10 5 21'%3E%3C/polyline%3E%3C/svg%3E"; }} />)
                        }
                      </div>
                      <div className="flex flex-col items-center justify-center w-full">
                        <div className="flex items-center justify-center w-full">
                        <span className="text-sm text-gray-600 text-center truncate max-w-xs">Current: {existingImage}</span>
                        </div>
                        <button type="button" onClick={removeImage} className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Remove Current</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <p className="text-sm text-gray-500">Click to upload image</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </label>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Brochure (PDF)</label>
              <div className="flex items-center">
                {formData.pdf ? (
                  <div className="relative w-full">
                    <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="mb-2 w-16 h-16 flex items-center justify-center overflow-hidden rounded-md bg-gray-100"><svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
                      <div className="flex flex-col items-center justify-center w-full">
                      <div className="flex items-center justify-center w-full">
                      <span className="text-sm text-gray-600 text-center truncate max-w-xs">{formData.brochure}</span>
                      </div>
                      <button type="button" onClick={removePdf} className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Remove</button>
                      </div>
                    </div>
                  </div>
                ) : existingPdf ? (
                  <div className="relative w-full">
                    <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="mb-2 w-16 h-16 flex items-center justify-center overflow-hidden rounded-md bg-gray-100"><svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
                      <div className="flex flex-col items-center justify-center w-full">
                      <div className="flex items-center justify-center w-full">
                      <span className="text-sm text-gray-600 text-center truncate max-w-xs">Current: {existingPdf}</span>
                      </div>
                      <button type="button" onClick={removePdf} className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Remove Current</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" /></svg>
                  <p className="text-sm text-gray-500">Click to upload PDF brochure</p>
                  </div>
                  <input type="file" accept=".pdf" onChange={handlePdfChange} className="hidden" />
                  </label>
                )}
              </div>
            </div>
            {Object.keys(initialSaw).filter(field => field !== 'image' && field !== 'pdf').map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium mb-1 capitalize">{field.replace(/_/g, ' ')}</label>
                {field === 'brochure' ?
                (<input type="text" name={field} value={formData[field] || existingPdf || ''} readOnly className="w-full p-2 border rounded bg-gray-100 hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors" placeholder="Generated after PDF upload" />)
                : field === 'work_bed_height' ?
                (<input type="number" name={field} value={formData[field] !== null ? formData[field] : ''} onChange={handleChange} className="w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors" placeholder={`Enter ${field.replace(/_/g, ' ')}`} />)
                : (field === "brand" || field === "model" || field === "price") ? (<input type="text" name={field} value={formData[field]} required onChange={handleChange} className="w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors" placeholder={`Enter ${field.replace(/_/g, ' ')}`} />)
                : (<input type="text" name={field} value={formData[field]} onChange={handleChange} className="w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors" placeholder={`Enter ${field.replace(/_/g, ' ')}`} />)
                }
              </div>
            ))}
          </div>
        </form>
      </div>
      <div className="p-4 border-t flex justify-end space-x-2">
        <button type="button" onClick={onClose} className="px-8 py-2 bg-gray-300 rounded hover:bg-gray-400" disabled={uploading}>Cancel</button>
        <button type="submit" form="update-saw-form" className="px-8 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={uploading}>{uploading ? 'Updating...' : 'Update'}</button>
      </div>
    </div>
  </Modal>
);

};

export default UpdateSaw;

